import { Component, Element, h, Host, Prop, State, Watch } from "@stencil/core";
import { getUniqueId } from "../../utils/unique-id";
import { debounce } from "../../utils/debounce";
import { formatPrefix, getCallingCodeForCountry, getValidator } from "./phone-input-validator";
import { CountryData, getCountryDataProvider, getDefaultLanguage } from "./regions";
import RegionItem from "./region-item";
import { dataValidation } from "../form/validation-attributes";

const baseClass = "ux-phone-input";

@Component({
    tag: "ux-phone-input",
    styleUrl: "phone-input.scss",
    shadow: false
})
export class PhoneInput {
    /** DOM node */
    @Element() domNode!: HTMLUxPhoneInputElement;

    /** Used for the input id */
    /* eslint-disable-next-line */
    @Prop({ mutable: true }) inputId?: string;
    /** Used to render an input label */
    @Prop() readonly label?: string = "";
    /** additional info label for the input field */
    @Prop() readonly labelInfo: string = "";
    /** Used for the input name */
    @Prop() readonly name?: string = "";
    /** Indicates that the field is disabled */
    @Prop() readonly disabled: boolean = false;
    /** Indicates that the field is mandatory */
    @Prop() readonly required: boolean = false;
    /** Indicates an invalid value. If set to true, an optional errorMsg is shown. */
    @Prop({ reflect: true, mutable: true }) error = false;
    /** Error message text that is rendered in error state */
    /* eslint-disable-next-line */
    @Prop({ mutable: true }) errorMsg?: string;
    /** value for the input */
    @Prop({ reflect: true, mutable: true }) value?: string = "";
    /** Language in which country names should be displayed */
    /* eslint-disable-next-line */
    @Prop() language: string = "";
    /** Default or detected phone number country code (ISO-2) */
    @Prop({ reflect: true, mutable: true }) country = "";
    /**
     * List of country codes to display in the country dropdown
     * (comma-separated, uppercase, e.g. "DE,FR,CN,IT,US"). If unset or empty, all available countries are shown.
     * */
    @Prop() readonly countryOptions?: string;

    /** DOM node of the input element */
    private inputElement: HTMLInputElement | null = null;
    private invalidMsg: string;
    private requiredMsg: string;

    @State() isDropdownOpen = false;
    @State() hasFocus = false;
    @State() menuItems: CountryData[] = [];
    @State() activeMenuItem = -1;

    @Watch("language")
    @Watch("countryOptions")
    setLanguage() {
        this.loadCountries();
    }

    @Watch("country")
    setCountry() {
        if (this.country.length > 1) {
            this.activeMenuItem = getOptionIndexForCountry(this.menuItems, this.country);
            const prefix = formatPrefix(getCallingCodeForCountry(this.country));
            // @ts-expect-error Check for typing
            if (this.value.substr(0, prefix.length) !== prefix) {
                this.value = prefix;
            }
        }
        if (this.initialCountry === null) {
            this.initialCountry = this.country;
        }
    }

    private setInitialValue() {
        if (this.value !== "") {
            // @ts-expect-error Check for typing
            this.updateValueAndCountry(this.value);
        }
    }

    @Watch("activeMenuItem")
    setActiveMenuItem() {
        if (this.menuItems[this.activeMenuItem] !== undefined) {
            this.updateDropdownMenu();
        }
    }

    @Watch("isDropdownOpen")
    handleDropdownOpen() {
        if (this.isDropdownOpen) {
            document.addEventListener("click", this.handleClickDoc);
        } else {
            document.removeEventListener("click", this.handleClickDoc);
        }
    }

    componentWillLoad() {
        if (this.inputId === undefined) {
            this.inputId = getUniqueId();
        }
        if (this.language === "") {
            this.language = getDefaultLanguage();
        }
        this.setLanguage();
        this.setInitialValue();
        this.setCountry();
        // @ts-expect-error Check for typing
        this.invalidMsg = this.domNode.getAttribute(dataValidation.invalid.msg);
        // @ts-expect-error Check for typing
        this.requiredMsg = this.domNode.getAttribute(dataValidation.required.msg);
    }

    disconnectedCallback() {
        document.removeEventListener("click", this.handleClickDoc);
    }

    // @ts-expect-error Check for typing
    private initialCountry: string = null;

    private loadCountries = async () => {
        try {
            const countries = await getCountryDataProvider(this.language);
            const options =
                this.countryOptions !== undefined ? this.countryOptions.split(/[^A-Z]/g).filter((str) => str.length === 2) : [];
            this.menuItems = countries.filter(({ country }) => options.length === 0 || options.includes(country));

            if (this.country === "CN") {
                this.menuItems = countries.filter(({ country }) => country !== "TW");
            }

            this.activeMenuItem = getOptionIndexForCountry(this.menuItems, this.country);
        } catch (e) {
            this.menuItems = [];
            this.activeMenuItem = -1;
        }
    };

    private updateValueAndCountry = (value: string, invalidate: boolean = false): void => {
        const validator = getValidator(value, this.country);
        this.value = validator.getFormattedValue();
        if (validator.isValid()) {
            const country = validator.getCountry();
            if (country !== undefined && country.length > 0) {
                this.country = country;
            }
        } else if (this.required && invalidate) {
            this.error = true;
        }
    };

    private updateDropdownMenu = debounce(() => {
        const container = this.domNode.querySelector(`.${baseClass}__dropdown`) as HTMLDivElement;
        if (container === null) {
            return;
        }
        if (this.activeMenuItem < 0) {
            return;
        }
        const item = container.querySelectorAll(".ux-phone-input__option")[this.activeMenuItem] as HTMLElement;
        if (item === undefined) {
            return;
        }
        const offsetTop: number = item.offsetTop;
        const offsetBottom: number = offsetTop + item.offsetHeight;
        const scrollTop: number = container.scrollTop;
        const scrollBottom: number = scrollTop + container.clientHeight;
        if (scrollTop > offsetTop) {
            container.scrollTop += offsetTop - scrollTop;
        } else if (scrollBottom < offsetBottom) {
            container.scrollTop += offsetBottom - scrollBottom;
        }
    }, 10);

    private toggleDropdownOpen = () => {
        this.isDropdownOpen = !this.isDropdownOpen;
        if (this.inputElement !== null) {
            this.inputElement.focus();
        }
        if (this.isDropdownOpen) {
            this.updateDropdownMenu();
        }
    };

    private handleClickDoc = (event: MouseEvent) => {
        if (!this.isDropdownOpen) {
            return;
        }
        if ((event.target as HTMLElement).closest("ux-phone-input") === this.domNode) {
            return;
        }
        this.isDropdownOpen = false;
    };

    private handleInputChange = (event: InputEvent) => {
        const input = event.target as HTMLInputElement;
        const fixedValue = input.value.replace(/[^0-9+\-() ]/g, "");
        if (input.value !== fixedValue) {
            input.value = fixedValue;
        }
        this.error = false;
        if (event.type === "input") {
            this.isDropdownOpen = false;
        }
        this.updateValueAndCountry(input.value, true);
    };

    private handleFocus = () => {
        // @ts-expect-error Check for typing
        this.errorMsg = null;
        this.hasFocus = true;
    };

    private handleBlur = (event: InputEvent) => {
        this.hasFocus = false;
        this.handleInputChange(event);
    };

    private handleSelectCountry = (country: string): void => {
        this.country = country;
        this.isDropdownOpen = false;
        if (this.inputElement !== null) {
            this.inputElement.focus();
        }
    };

    private handleKeyPress = (event: KeyboardEvent) => {
        const { activeMenuItem, isDropdownOpen, menuItems } = this;
        const isInvalidChar = event.key.length === 1 && /[^0-9+\-() ]/i.test(event.key);

        switch (event.key) {
            case "Enter":
                if (isDropdownOpen) {
                    event.preventDefault();
                    if (menuItems[activeMenuItem] !== undefined) {
                        this.handleSelectCountry(menuItems[activeMenuItem].country);
                    }
                }
                break;
            case "ArrowDown":
                event.preventDefault();
                if (menuItems.length === 0) {
                    return;
                }
                if (!isDropdownOpen) {
                    this.isDropdownOpen = true;
                } else {
                    this.activeMenuItem = Math.min(this.menuItems.length - 1, this.activeMenuItem + 1);
                }
                break;
            case "ArrowUp":
                event.preventDefault();
                if (menuItems.length === 0) {
                    return;
                }
                if (!isDropdownOpen) {
                    this.isDropdownOpen = true;
                } else {
                    this.activeMenuItem = Math.max(0, this.activeMenuItem - 1);
                }
                break;
            case "Escape":
                if (isDropdownOpen) {
                    event.preventDefault();
                    this.isDropdownOpen = false;
                }
                break;
            default:
                if (isInvalidChar && !event.metaKey) {
                    event.preventDefault();
                    if (isDropdownOpen) {
                        const char: string = event.key.toLowerCase();
                        const menuItem = this.menuItems.filter(({ label }) => label.substr(0, 1).toLowerCase() === char)[0];
                        if (menuItem !== undefined) {
                            this.activeMenuItem = this.menuItems.indexOf(menuItem);
                        }
                    }
                }
                break;
        }
    };

    render() {
        const {
            initialCountry,
            country,
            disabled,
            error,
            errorMsg,
            hasFocus,
            inputId,
            isDropdownOpen,
            label,
            labelInfo,
            menuItems,
            name,
            required,
            value,
            invalidMsg,
            requiredMsg
        } = this;
        const validator = getValidator(value, country);
        // @ts-expect-error Check for typing
        const hasNumber = value.trim().length > validator.getPrefix().length;
        const hasMenuItems = menuItems.length > 0;
        const isInvalid = !error && hasNumber ? !validator.isValid() : error;
        let errorMessage = hasNumber && isInvalid ? errorMsg || invalidMsg : errorMsg || requiredMsg;
        // @ts-expect-error Check for typing
        errorMessage = errorMessage === "" ? null : errorMessage;
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--disabled`]: disabled,
                    [`${baseClass}--required`]: required,
                    [`${baseClass}--error`]: isInvalid,
                    [`${baseClass}--active`]: hasFocus || isDropdownOpen,
                    [`${baseClass}--dropdown-opened`]: isDropdownOpen
                }}
            >
                <ux-input-label
                    text={label}
                    inputId={inputId}
                    error={isInvalid}
                    errorMsg={errorMessage}
                    required={required}
                    labelInfo={labelInfo}
                >
                    <slot />
                </ux-input-label>
                <div class={`${baseClass}__inner`}>
                    <input
                        autoComplete="off"
                        autoCorrect="off"
                        class={{
                            [`ux-input`]: true,
                            [`${baseClass}__input`]: true
                        }}
                        inputMode="tel"
                        disabled={disabled}
                        id={inputId}
                        onInput={this.handleInputChange}
                        onFocus={this.handleFocus}
                        // @ts-expect-error Check for typing
                        onBlur={this.handleBlur}
                        onKeyDown={this.handleKeyPress}
                        // @ts-expect-error Check for typing
                        ref={(el) => (this.inputElement = el)}
                        type="tel"
                        value={value}
                        aria-invalid={isInvalid ? "true" : "false"}
                    />
                    <input
                        name={name}
                        required={required}
                        style={{ display: "none" }}
                        autoComplete="off"
                        autoCorrect="off"
                        aria-hidden="true"
                        type="text"
                        disabled={disabled}
                        value={validator.getValidatedValue()}
                    />
                    <button
                        class={{
                            [`${baseClass}__dropdown-button`]: true,
                            [`${baseClass}__dropdown-button--disabled`]: disabled || !hasMenuItems
                        }}
                        onClick={hasMenuItems ? this.toggleDropdownOpen : undefined}
                        type="button"
                    >
                        <ux-country-icon country={initialCountry === "CN" && country === "TW" ? "" : country} />

                        <ux-icon name="chevron-down" weight="bold" />
                    </button>
                </div>
                {hasMenuItems && isDropdownOpen && (
                    <div class={`${baseClass}__dropdown`}>
                        <ul role="listbox">
                            {this.menuItems.map((region, index) => (
                                <RegionItem
                                    active={index === this.activeMenuItem}
                                    baseClass={baseClass}
                                    onSelect={this.handleSelectCountry}
                                    region={region}
                                />
                            ))}
                        </ul>
                    </div>
                )}
            </Host>
        );
    }
}

const getOptionIndexForCountry = (options: CountryData[], country: string): number => {
    let optionIndex = -1;
    if (options.length === 0 || country.length < 2) {
        return optionIndex;
    }
    options.forEach((option, index) => {
        if (optionIndex === -1 && option.country.valueOf() === country) {
            optionIndex = index;
        }
    });
    return optionIndex;
};
